import React, { useState, useEffect, useMemo } from 'react';
import { Search, Plus } from 'lucide-react';
import { useNavigate, useLocation } from "react-router-dom";
import styles from './AdminHome.module.css';
import CategoryButton from "@/ui/components/client/CategoryButton/CategoryButton.js";
import AdminProductList from "@/ui/components/admin/AdminProductList/AdminProductList.js";
import RecipeList from "@/ui/components/admin/AdminRecipeList/RecipeList.js";
import RecipeDetail from "@/ui/components/admin/AdminRecipeDetail/RecipeDetail.js";
import { ROUTES } from '@/config/routes';
import { Categories } from "@/data/categories.js";
import { getRecipeListLocalService } from '@services/adminRecipeBookLocalService';
import { getAdminRecipeService } from '@services/adminRecipeService';

let cachedProducts = null;

function HomePage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [viewMode] = useState('productos');
    const [activeCategory, setActiveCategory] = useState(() => {
        return sessionStorage.getItem('adminActiveCategory') || Categories[0].id;
    });
    const [searchTerm, setSearchTerm] = useState(() => {
        return sessionStorage.getItem('adminSearchTerm') || "";
    });
    const [selectedRecipe, setSelectedRecipe] = useState(null);
    const [products, setProducts] = useState(cachedProducts || []);
    const [localRecipes, setLocalRecipes] = useState([]);

    useEffect(() => {
        if (!cachedProducts) {
            getAdminRecipeService()
                .then(data => {
                    cachedProducts = data;
                    setProducts(data);
                })
                .catch(error => console.error("Error al cargar productos:", error));
        }
        setLocalRecipes(getRecipeListLocalService());
    }, []);

    useEffect(() => {
        sessionStorage.setItem('adminActiveCategory', activeCategory);
    }, [activeCategory]);

    useEffect(() => {
        sessionStorage.setItem('adminSearchTerm', searchTerm);
    }, [searchTerm]);

    useEffect(() => {
        const handleScroll = () => {
            sessionStorage.setItem('adminScrollTop', String(window.scrollY));
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (products.length > 0) {
            const savedScroll = sessionStorage.getItem('adminScrollTop');
            if (savedScroll) {
                const scrollTimeout = setTimeout(() => {
                    window.scrollTo(0, Number(savedScroll));
                }, 100);
                return () => clearTimeout(scrollTimeout);
            }
        }
    }, [products]);

    useEffect(() => {
        if (location.state?.newProduct) {
            const newProd = location.state.newProduct;
            if (cachedProducts) {
                cachedProducts = [newProd, ...cachedProducts];
                setProducts(cachedProducts);
            } else {
                setProducts(prev => [newProd, ...prev]);
            }
            navigate(location.pathname, { replace: true, state: {} });
        }
        if (location.state?.updatedProduct) {
            const updated = location.state.updatedProduct;
            if (cachedProducts) {
                cachedProducts = cachedProducts.map(p => p.id === updated.id ? { ...p, ...updated } : p);
                setProducts(cachedProducts);
            } else {
                setProducts(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
            }
            navigate(location.pathname, { replace: true, state: {} });
        }
        if (location.state?.deletedProductId) {
            const deletedId = location.state.deletedProductId;
            if (cachedProducts) {
                cachedProducts = cachedProducts.filter(p => p.id !== deletedId);
                setProducts(cachedProducts);
            } else {
                setProducts(prev => prev.filter(p => p.id !== deletedId));
            }
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, navigate, location.pathname]);

    const filteredItems = useMemo(() => {
        const search = (searchTerm || "").trim().toLowerCase();

        if (viewMode === 'recetas') {
            return (localRecipes || []).filter(r => {
                if (!search) return true;
                const keywords = search.split(/\s+/).filter(Boolean);
                const recipeName = (r.name || "").toLowerCase();
                return keywords.every(kw => recipeName.includes(kw));
            });
        } else {
            let result = [...(products || [])];

            if (search) {
                const keywords = search.split(/\s+/).filter(Boolean);
                return result.filter(p => {
                    const productName = (p.name || "").toLowerCase();
                    const productDesc = (p.description || "").toLowerCase();
                    return keywords.every(kw => productName.includes(kw) || productDesc.includes(kw));
                });
            }

            if (activeCategory === 'cat-popular-001') {
                result = result
                    .filter(p => Number(p.likes_count) > 0)
                    .sort((a, b) => Number(b.likes_count) - Number(a.likes_count));
            }
            else if (activeCategory !== 'all' && activeCategory !== Categories[0].id) {
                result = result.filter(p => p.category === activeCategory);
            }

            return result;
        }
    }, [viewMode, searchTerm, activeCategory, localRecipes, products]);

    if (selectedRecipe) {
        return <RecipeDetail recipe={selectedRecipe} onBack={() => setSelectedRecipe(null)} />;
    }

    return (
        <div className={styles.appContainer}>
            <header className={styles.headerDiseno}>
                <h1 className={styles.mainTitle}>
                    {viewMode === 'recetas' ? 'Recetas Locales' : 'Productos Locales'}
                </h1>
            </header>

            <div className={styles.searchContainer}>
                <div className={styles.searchBox}>
                    <Search size={20} className={styles.searchIcon} />
                    <input
                        type="text"
                        placeholder={`Buscar ${viewMode}...`}
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            sessionStorage.setItem('adminScrollTop', '0');
                            window.scrollTo(0, 0);
                        }}
                    />
                </div>
            </div>

            {viewMode === 'productos' && (
                <nav className={styles.categoriesScroll}>
                    {Categories.map((category) => (
                        <CategoryButton
                            key={category.id}
                            category={category}
                            isActive={activeCategory === category.id}
                            onClick={() => {
                                setActiveCategory(category.id);
                                sessionStorage.setItem('adminScrollTop', '0');
                                window.scrollTo(0, 0);
                            }}
                        />
                    ))}
                </nav>
            )}

            <main className={styles.contentSection}>
                {filteredItems.length > 0 ? (
                    viewMode === 'recetas' ? (
                        <RecipeList
                            recipes={filteredItems}
                            onSelectRecipe={(recipe) => setSelectedRecipe(recipe)}
                        />
                    ) : (
                        <AdminProductList products={filteredItems} />
                    )
                ) : (
                    <p className={styles.emptyMessage}>No se encontraron {viewMode}</p>
                )}
            </main>

            <footer className={styles.footerNav}>
                {/*<button*/}
                {/*    className={`${styles.navItem} ${viewMode === 'recetas' ? styles.activeTab : ''}`}*/}
                {/*    onClick={() => { setViewMode('recetas'); setSearchTerm(""); }}*/}
                {/*>*/}
                {/*    Recetas*/}
                {/*</button>*/}

                <div className={styles.addButtonContainer}>
                    <button
                        className={styles.addButtonCircle}
                        onClick={() => navigate(viewMode === 'recetas' ? ROUTES.ADMIN_ADD_RECIPE : ROUTES.ADMIN_PRODUCT_EDIT)}
                        aria-label="Agregar contenido"
                    >
                        <Plus size={28} />
                    </button>
                </div>

                {/* <button
                    className={`${styles.navItem} ${viewMode === 'productos' ? styles.activeTab : ''}`}
                    onClick={() => { setViewMode('productos'); setSearchTerm(""); }}
                >
                    Productos
                </button> */}
            </footer>
        </div>
    );
}

export default HomePage;